import React, { useEffect, useLayoutEffect, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
import Hero from "src/components/Hero/Hero";
import ReactMarkdown from "react-markdown";
import TitleSection from "src/components/TitleSection/TitleSection";
import CashGamesInfo from "src/components/Poker/CashGamesInfo";
import EventFinished from "src/components/EventFinished";
import Button from "src/components/Button/Button";
import ImageLink from "src/components/ImageWrapper/ImageLink";
import { Col, Row } from "react-bootstrap";
import { BiSolidDollarCircle } from "react-icons/bi";
import { LuAlarmClock } from "react-icons/lu";
import { useErs } from "src/context/ers/ers.provider";
import { formatAmount } from "src/utils/formatters";
import { getScheduleDates, shouldPublish } from "src/utils/publishUtils";
import { HIGH_HAND_TAB_HASH } from "src/utils/highHandUtils";
import { buildHighHandSectionsFromFlat } from "src/utils/mapHighHand";

const PERIOD_ICONS = {
  BiSolidDollarCircle,
  LuAlarmClock,
};

const periodIcon = (name, fallback) => PERIOD_ICONS[name] || fallback;

/**
 * Shared High Hand page body for JSON runtime and Payload Live Preview.
 *
 * @param {Object} props
 * @param {object} props.page - mapped page from mapHighHandDoc (or compatible)
 * @param {boolean} [props.forceShowCashPrizes] - ignore schedule gate (preview)
 */
const HighHandPageContent = ({ page, forceShowCashPrizes = false }) => {
  const { i18n, t } = useTranslation();
  const location = useLocation();
  const { data, fetchData } = useErs();
  const lng = i18n.language === "fr" ? "fr" : "en";

  const tabs = page?.tabs;

  const tabKeyFromHash = () => {
    const hash = window.location.hash.replace("#", "");
    if (!hash || !tabs?.length) return tabs?.[0]?.tabKey || "highHand";
    const labels = HIGH_HAND_TAB_HASH[lng] || HIGH_HAND_TAB_HASH.en;
    const fromHash = Object.keys(labels).find((key) => labels[key] === hash);
    if (fromHash && tabs.some((tab) => tab.tabKey === fromHash)) return fromHash;
    if (tabs.some((tab) => tab.tabKey === hash)) return hash;
    return tabs[0].tabKey;
  };

  const [tabKey, setTabKey] = useState(tabKeyFromHash);

  useEffect(() => {
    if (tabs?.length) {
      const stillValid = tabs.some((tab) => tab.tabKey === tabKey);
      if (!stillValid) setTabKey(tabs[0].tabKey);
    }
  }, [tabs, tabKey]);

  useLayoutEffect(() => {
    setTabKey(tabKeyFromHash());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL hash / locale
  }, [location.hash, lng, tabs]);

  useLayoutEffect(() => {
    if (!tabs?.length || tabs.length < 2) return;
    const labels = HIGH_HAND_TAB_HASH[lng] || HIGH_HAND_TAB_HASH.en;
    if (tabKey !== "highHand") {
      const translated = labels[tabKey] || tabKey;
      window.history.replaceState(
        null,
        "",
        `${location.pathname}#${translated}`,
      );
    } else {
      window.history.replaceState(null, "", location.pathname);
    }
  }, [tabKey, location.pathname, lng, tabs]);

  const handleTabSelect = (k) => {
    if (k) setTabKey(k);
  };

  const activeContent =
    tabs?.find((tab) => tab.tabKey === tabKey)?.content || page?.content;

  const ersKey = activeContent?.cashPrizesErsKey || "mystery-high-hand";

  useEffect(() => {
    if (activeContent?.cashPrizes) {
      fetchData(ersKey);
    }
  }, [fetchData, ersKey, activeContent?.cashPrizes, i18n.language]);

  if (!activeContent) return null;

  const addDollarSign = (amount) => formatAmount(amount, lng);
  const cashPrizesData = data[ersKey] || [];
  const schedule = getScheduleDates(page?.promotionKey);
  const showCashPrizes =
    activeContent.cashPrizes &&
    (forceShowCashPrizes || shouldPublish(schedule));

  const shouldHideTitle =
    page?.promotionKey === "highHandSummer2026" &&
    new Date() >= new Date("2026-05-25");

  const sections = Array.isArray(activeContent.sections)
    ? activeContent.sections
    : buildHighHandSectionsFromFlat(activeContent);

  const hero =
    sections.find((section) => section.type === "hero") ||
    activeContent.hero ||
    {};
  const bodySections = sections.filter((section) => section.type !== "hero");

  const renderSection = (section, index) => {
    switch (section.type) {
      case "titleSection":
        return (
          <TitleSection
            key={`section-${index}`}
            as={section.titleAs || "h2"}
            title={section.title}
            text={section.text}
            borderColor={section.highlightColor}
            textFontSize={section.textFontSize}
            backgroundColor={section.bgColor}
          />
        );
      case "howItWorks":
        return (
          <div className="how-it-works" key={`section-${index}`}>
            <ReactMarkdown>{section.title}</ReactMarkdown>
            <ReactMarkdown>{section.text}</ReactMarkdown>
          </div>
        );
      case "textBlock":
        return (
          <div className="how-it-works" key={`section-${index}`}>
            <ReactMarkdown>{section.text}</ReactMarkdown>
          </div>
        );
      case "cashPrizes":
        if (!showCashPrizes) return null;
        return (
          <div className="cash-prizes" key={`section-${index}`}>
            <h4>{t("poker.mystery-high-hand.cash-prize-available")}</h4>
            <table>
              <thead>
                <tr>
                  <th>{t("poker.mystery-high-hand.remaining")}</th>
                  <th>{t("poker.mystery-high-hand.amount")}</th>
                </tr>
              </thead>
              <tbody>
                {cashPrizesData?.length ? (
                  cashPrizesData.map(
                    (item, rowIndex) =>
                      item?.amount !== "" && (
                        <tr key={rowIndex}>
                          <td>{item.remaining}</td>
                          <td>{addDollarSign(item.amount)}</td>
                        </tr>
                      ),
                  )
                ) : (
                  <>
                    <tr>
                      <td></td>
                      <td>$1,000</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>$800</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>$400</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td>$200</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>
        );
      case "twoColumnPeriods":
        const LeftPeriodIcon = periodIcon(
          section.leftColumnIcon,
          LuAlarmClock,
        );
        const RightPeriodIcon = periodIcon(
          section.rightColumnIcon,
          BiSolidDollarCircle,
        );
        return (
          <Row className="periods pb-3" key={`section-${index}`}>
            <Col>
              <LeftPeriodIcon />
              <TitleSection
                as={"h3"}
                title={section.qualifyingPeriods?.title}
                text={section.qualifyingPeriods?.text}
                className="py-0"
              />
            </Col>
            <Col>
              <RightPeriodIcon />
              <TitleSection
                as={"h3"}
                title={section.payoutPeriods?.title}
                text={section.payoutPeriods?.text}
                className="py-0"
              />
            </Col>
          </Row>
        );
      case "smallInfo":
        return (
          <ReactMarkdown key={`section-${index}`}>{section.text}</ReactMarkdown>
        );
      case "elevatedTournamentLineup":
        return (
          <div className="how-it-works" key={`section-${index}`}>
            <ReactMarkdown>{section.title}</ReactMarkdown>
            <ReactMarkdown>{section.text}</ReactMarkdown>
            {section.link && (
              <Button
                className={"mt-2 mb-2"}
                href={t("routes.pokerTournamentCalendar")}
                bgColor="var(--red)"
                color="var(--white)"
                hoverColor="var(--black)"
                hoverBgColor="var(--white)"
                label={section.link}
                size="medium"
              />
            )}
          </div>
        );
      case "titan":
        return (
          <Row className="d-flex" key={`section-${index}`}>
            {section.image ? (
              <StyledCol xs={12} md={6}>
                <ImageLink
                  to={t("routes.titanTuesday2026")}
                  imgSrc={section.image}
                  alt={section.title || "Titan Tuesday"}
                  className="card-image-top"
                />
              </StyledCol>
            ) : null}
            <StyledCol xs={12} md={section.image ? 6 : 12}>
              {section.title ? (
                <ReactMarkdown>{section.title}</ReactMarkdown>
              ) : null}
              <ReactMarkdown>{section.text}</ReactMarkdown>
              <Button
                className={"mt-0 mb-2"}
                href={t("routes.titanTuesday2026")}
                bgColor="var(--white)"
                color="var(--black)"
                hoverColor="var(--black)"
                hoverBgColor="var(--white)"
                label={t("button.learn-more")}
                size="medium"
              />
            </StyledCol>
          </Row>
        );
      case "nationalPokerDay":
        return (
          <Row className="d-flex" key={`section-${index}`}>
            <StyledCol xs={12} md={section.image ? 6 : 12}>
              {section.title ? (
                <ReactMarkdown>{section.title}</ReactMarkdown>
              ) : null}
              <ReactMarkdown>{section.text}</ReactMarkdown>
              <Button
                className={"mt-0 mb-2"}
                href={t("highHands.nationalPokerDay2025")}
                bgColor="var(--white)"
                color="var(--black)"
                hoverColor="var(--black)"
                hoverBgColor="var(--white)"
                label={t("button.learn-more")}
                size="medium"
              />
            </StyledCol>
            {section.image ? (
              <StyledCol xs={12} md={6}>
                <ImageLink
                  to={t("highHands.nationalPokerDay2025")}
                  imgSrc={section.image}
                  alt={section.title || "National Poker Day"}
                  className="card-image-top"
                />
              </StyledCol>
            ) : null}
          </Row>
        );
      case "cashGamesInfo":
        return <CashGamesInfo key={`section-${index}`} />;
      case "generalRules":
        return (
          <div className="general-rules" key={`section-${index}`}>
            <h4>{section.title}</h4>
            <ReactMarkdown>{section.list}</ReactMarkdown>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Div hideTitle={shouldHideTitle}>
      <Hero
        image={hero.image}
        bgImage={hero.bgImage}
        fluidImage={
          hero.fluidImage !== undefined ? Boolean(hero.fluidImage) : !hero.image
        }
      />
      {page?.promotionKey && (
        <EventFinished
          scheduleKeyName={page.promotionKey}
          eventType="promotion"
        />
      )}
      <container-div>
        {tabs?.length > 1 && (
          <Tabs
            id="high-hand-payload-tabs"
            activeKey={tabKey}
            onSelect={handleTabSelect}
            className="gap-2 mt-5"
            variant="pills"
          >
            {tabs.map((tab) => (
              <Tab
                eventKey={tab.tabKey}
                title={tab.label}
                key={tab.tabKey}
              />
            ))}
          </Tabs>
        )}
        <div className="high-hand">
          <div className="high-hand-body">{bodySections.map(renderSection)}</div>
        </div>
      </container-div>
    </Div>
  );
};

const Div = styled.div`
  width: 100%;
  position: relative;
  background-color: #010101;
  .hero_text_container {
    display: none;
  }
  .title-section {
    padding: 40px 0 0 0;
    ul li {
      text-align: left;
    }
    strong {
      color: #cf3c22;
    }
    ${(props) =>
      props.hideTitle &&
      `
      h3 {
        display: none;
      }
    `}
  }
  div.periods {
    text-align: center;
    svg {
      margin: 20px 0 10px;
      color: var(--red);
      font-size: 40px;
    }
  }
  .high-hand {
    margin: 20px 0;
    .high-hand-body {
      width: 100%;
      max-width: 1440px;
      height: auto;
      margin: 0 auto;
      color: var(--white);
      & > h3 {
        font-weight: 700;
        font-size: 20px;
        margin-top: 0px;
        padding-bottom: 20px;
        text-transform: uppercase;
      }
      & > h4 {
        background: rgb(170, 11, 11);
        background: linear-gradient(
          45deg,
          rgba(170, 11, 11, 1) 0%,
          rgba(0, 0, 0, 1) 50%,
          rgba(170, 11, 11, 1) 100%
        );
        color: var(--white);
        font-weight: 700;
        text-transform: uppercase;
        text-align: center;
        padding: 15px;
        max-width: 500px;
        font-size: 30px;
        margin: 20px auto;
        border-radius: 5px;
        line-height: 25px;
      }
      & > p {
        margin: 0 0 10px;
        font-size: 18px;
        color: #ffffff;
        text-align: center;
        strong {
          color: #cf3c22;
        }
      }
    }
    .general-rules {
      background-color: #111;
      width: 100%;
      max-width: 1440px;
      margin: 40px auto 8rem auto;
      padding: 2rem 1rem 5rem;
      & > h4 {
        font-weight: 700;
        color: #999;
        text-transform: uppercase;
        text-align: center;
        padding: 15px;
        font-size: 30px;
        margin: 5px 0 10px 0;
        background: none;
        max-width: none;
        border-radius: 0;
        line-height: normal;
      }
      & > ul,
      & > ol {
        margin-top: 0;
        margin-bottom: 10px;
        padding-left: 1.5rem;
        & > li {
          font-size: 16px;
          color: #999;
          white-space: normal;
          ul,
          ol {
            list-style: lower-alpha;
            padding-left: 1.5rem;
            margin-top: 0.5rem;
            ul {
              list-style: lower-roman;
            }
          }
        }
      }
    }
  }
  .how-it-works {
    margin: 20px auto;
    display: flex;
    flex-direction: column;
    justify-content: center;
    background-color: var(--lightblack);
    padding: 40px;
    ul {
      list-style: disc;
      text-align: left;
      padding-left: 40px;
      margin-bottom: 1rem;
      li {
        margin-bottom: 10px;
      }
    }
    p,
    h1,
    h2,
    h3,
    h4 {
      color: var(--white);
      text-align: center;
    }
    strong {
      color: #cf3c22;
    }
  }
  .cash-prizes {
    & > h4 {
      background: rgb(170, 11, 11);
      background: linear-gradient(
        45deg,
        rgba(170, 11, 11, 1) 0%,
        rgba(0, 0, 0, 1) 50%,
        rgba(170, 11, 11, 1) 100%
      );
      color: var(--white);
      font-weight: 700;
      text-transform: uppercase;
      text-align: center;
      padding: 15px;
      max-width: 500px;
      font-size: 30px;
      margin: 20px auto;
      border-radius: 5px;
      line-height: 25px;
    }
    & > table {
      width: 100%;
      max-width: 100%;
      margin: 20px 0px 40px 0px;
      background: var(--black);
      color: var(--white);
      border: 1px solid var(--lightblack);
      border-spacing: 0;
      border-collapse: collapse;
      & > thead {
        & > tr {
          & > th {
            text-align: center;
            font-size: 20px;
            font-weight: 400;
            width: 50%;
            background-color: #333;
            border: 1px solid #222;
            padding: 8px;
            line-height: 1.42857143;
          }
        }
      }
      & > tbody {
        & > tr {
          & > td:first-child {
            text-align: center;
            font-size: 30px;
            font-weight: 700;
            width: 50%;
            color: var(--white);
            background-color: #230202;
            border: 1px solid #222;
            padding: 8px;
            line-height: 1.42857143;
          }
          & > td:last-child {
            background-color: #aa0b0b;
            text-align: center;
            font-size: 30px;
            font-weight: 700;
            width: 50%;
            color: #fff;
            border: 1px solid #222;
            padding: 8px;
            line-height: 1.42857143;
          }
        }
      }
    }
  }
  .nav-link,
  .nav-pills .nav-link.active,
  .nav-pills .show > .nav-link {
    padding: 1rem;
    background-color: ${(props) =>
      props.bgColor || "var(--mutedred)"} !important;
    color: ${(props) => props.textColor || "white"};
    border-right: 1px solid
      ${(props) => props.borderColor || "var(--lightblack)"};
    border-bottom: 1px solid
      ${(props) => props.borderColor || "var(--lightblack)"};
    &:hover,
    &.active,
    &:focus {
      background-color: ${(props) => props.active || "var(--red)"} !important;
      color: ${(props) => props.textColor || "var(--white)"};
    }
  }
  .nav-pills {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .nav-link {
    width: 100%;
  }
  @media screen and (max-width: 991px) {
    .nav-pills {
      grid-template-columns: 1fr;
    }
    .how-it-works {
      padding: 20px;
    }
  }
`;

const StyledCol = styled(Col)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  p {
    font-size: 1.8rem;
    margin-bottom: 20px;
  }
  @media screen and (max-width: 991px) {
    p {
      font-size: 1.2rem;
    }
  }
`;

export default HighHandPageContent;
